import * as React from 'react';
import { Action, AnyAction, Dispatch, MiddlewareAPI } from 'redux';
import {
  ContentSetup,
  ContentType,
  ModuleInfo,
  toSFFProblemType
} from '@/utils';
import { activityStart } from '@/redux/actions/embedding';
import {
  forceProblemType,
  nextProblemChosen as nextPageChosen,
  nextProblemTypeReached,
  nextPageTypeAction,
  progressionEndReached,
  progressStateLoaded,
  progressStateLoading,
  responseEvaluated,
  getModuleTips,
  resetMastery,
  switchModule
} from '@/redux/actions/progression';
import {
  moveOnToNextPage,
  problemLoaded,
  submitResponse,
  introShown,
  networkFailure,
  logEvent,
  duplicateInstanceDetected
} from '@/redux/actions/ui';
import { getOptionsToIgnore } from '@/redux/selectors/progression';
import { State } from '@/redux/state';
import { saveProgress } from './persistance/remote';

import {
  setSideBarOpenPanel,
  setSideBarDisabled,
  Panel,
  setSideBarElement,
  setSideBarStyle
} from '@/sidebar-state';

import ReactMarkdown from 'react-markdown';
import { setSkillBarName } from '@/redux/actions/skill-bar';
import {
  getContentTypeFromSetup,
  getPageTypeFromSetup
} from '@/utils/utils';
import {
  ITSEventType,
  SFFProblemType,
  RawPageData,
  PlayStats,
  RawIntroPageData,
  GetProblemRequest,
  ProblemGenerator,
  ItsLoggerEvent
} from 'its-pages';
import { MASTERY_THRESHOLD } from '@/redux/reducers/ui';
import { APP_CFG } from '@/appcfg';
import { CURRENT_STATE_VERSION } from './api';

export const createStartProblem = (moduleInfo: ModuleInfo): ContentSetup => ({
  moduleInfo,
  difficulty: {
    main: 0,
    secondary: 0
  },
  scenario: undefined,
  seed: undefined,
  progressionIndex: 0
});

type NextPageResult = ContentSetup | 'END';
const END = 'END';

/**
 * Attempt to generate the next question 'page' within the user's progression or signify
 * they are done with all questions
 * 
 * @param problemSetup 
 * @returns
 *    Data to setup the next question 'page' or the tag to indicate end of progression
 */
const nextPageType = (problemSetup: ContentSetup): NextPageResult => {
  const {
    difficulty: { main, secondary },
    progressionIndex,
    moduleInfo: { progression },
    moduleInfo
  } = problemSetup;

  let newProgressionIndex = progressionIndex;
  let newMain = main;
  let newSecondary = secondary + 1;

  const bumpType = () => {
    newProgressionIndex++;
    newMain = 0;
    newSecondary = 0;
  };

  const maxSecondary = progression[progressionIndex].subs;

  if (typeof maxSecondary === 'number') {
    if (newSecondary > maxSecondary) {
      bumpType();
    }
  } else {
    if (newSecondary > maxSecondary[main]) {
      newSecondary = 0;
      newMain += 1;
      if (newMain >= maxSecondary.length) {
        newMain = 0;
        newProgressionIndex++;
      }
    }
  }

  if (newProgressionIndex >= progression.length) {
    return END;
  }

  return {
    ...problemSetup,
    difficulty: {
      main: newMain,
      secondary: newSecondary
    },
    progressionIndex: newProgressionIndex,
    moduleInfo
  };
};

/**
 * Only move to the next difficulty level if we have at least two
 * correct (score = 1.0) responses at the current difficulty level.
 * @param state State
 * @param setup ContentSetup
 * @return true if we can move to next difficulty level
 */
const maybeProgressToNextLevel = (state: State, setup: ContentSetup) => {
  const pageType = getPageTypeFromSetup(setup);
  const contentType = getContentTypeFromSetup(setup);

  switch (pageType) {
    case 'banner': {
      if (contentType === ContentType.ModuleCompletion) {
        return false;
      } else {
        return true;
      }
    }

    case 'problem':
      switch (contentType) {
        // For peer evaluation, just have a fixed number of questions the user will see,
        // since there is no way to check correctness
        case ContentType.PeerEval:
          let numCompleted = 0;
          state.progression.problemsDone.forEach((value, key) => {
            if (key.includes(ContentType.PeerEval)) {
              numCompleted += value.length;
            }
          });
          return numCompleted > 2;
        default: {
          return state.progression.masteryRollingAverage >= MASTERY_THRESHOLD;
          // Ensure that no question type is asked too many times
          // return responses.length >= APP_CFG.moveonCount || countCorrect >= 2;
        }
      }
  }
};

const generateContentFromSetup = async (
  store: MiddlewareAPI<Dispatch<AnyAction>>,
  contentSetup: ContentSetup
) => {
  const requestFailed = false;
  let rawPageData: RawPageData | null | undefined = null;

  store.dispatch(nextPageChosen(contentSetup));

  const contentType = getContentTypeFromSetup(contentSetup);
  const optionsToIgnore = getOptionsToIgnore(store.getState(), contentSetup);
  // IMPORTANT: For now, rely and the random seed value embedded inside the problem generator instance.
  // If we want a reproduceable sequence, need to alter this
  const request: GetProblemRequest = {
    ignore: optionsToIgnore,
    level: contentSetup.difficulty,
    skill: contentSetup.moduleInfo.skill.name,
    type: toSFFProblemType[contentType],
    requiredCorrectTag: undefined,
    requiredIncorrectTag: undefined,
    seed: -1
  };

  try {
    rawPageData = await ProblemGenerator.getProblem(request);
  } catch (e) {
    console.warn('Duplicate Instance Detected!');
    store.dispatch(duplicateInstanceDetected());
    return;
  }

  if (rawPageData) {
    const state = store.getState();
    const level = contentSetup.progressionIndex;
    const playStats: PlayStats = {
      userId: state.ui.userId || '',
      sessionId: state.progression.sessionId,
      level,
      score: 0,
      cumulativeTime: 0,
      events: [],
      tagResults: []
    };
    rawPageData.playStats = playStats;
  }

  if (!requestFailed) {
    setSideBarDisabled(Panel.All, true);
    setSideBarStyle(Panel.All, 'default');
    setSideBarOpenPanel(Panel.None);

    if (rawPageData) {
      // Trigger the rendering of the question/info page with the given parameters
      store.dispatch(problemLoaded(rawPageData));
    } else {
      // If couldn't generate a question/problem with given context/setup data just move to a different
      // category or tier. (This really shouldn't trigger outside of the stand alone game when testing
      // question types from the menu dropdown that don't exist for a skill)
      bumpDifficulty(store, contentSetup);
    }
  } else {
    store.dispatch(networkFailure());
  }
};

/**
 * Attempt to generate the next question 'page' in user progression that is higher difficulty.
 * This should return a question that is in a different category or in the same category but with higher
 * difficulty tier.
 * 
 * @param store 
 * @param problemSetup 
 * @returns 
 */
const bumpDifficulty = (
  store: MiddlewareAPI<Dispatch<AnyAction>>,
  problemSetup: ContentSetup
) => {
  const nextPageTypeSetup = nextPageType(problemSetup);
  if (nextPageTypeSetup === END) {
    store.dispatch(progressionEndReached(true));
  } else {
    store.dispatch(nextPageTypeAction(nextPageTypeSetup));
  }
  store.dispatch(resetMastery());
  return;
};

/**
 * Request to get next question (i.e. next page with the question)
 * 
 * @param store 
 * @returns 
 */
const getNextPage = async (store: MiddlewareAPI<Dispatch<AnyAction>>) => {
  const state = store.getState();
  const {
    currentProblemState: { setup: currentProblemSetup },
    progression: { completed, moduleInfo }
  } = state;

  if (completed) {
    return;
  }
  let problemSetup: ContentSetup;

  if (!currentProblemSetup) {
    problemSetup = createStartProblem(moduleInfo);
  } else {
    if (maybeProgressToNextLevel(state, currentProblemSetup)) {
      bumpDifficulty(store, currentProblemSetup);
      return;
    }
    problemSetup = currentProblemSetup;
  }

  generateContentFromSetup(store, problemSetup);
};

async function getSkillTips(store: MiddlewareAPI<Dispatch<AnyAction>>) {
  const state = store.getState();
  const {
    progression: { moduleInfo }
  } = state;

  const request: GetProblemRequest = {
    ignore: [],
    level: { main: 0, secondary: 0 },
    skill: moduleInfo.skill.name,
    type: SFFProblemType.ModuleIntro,
    seed: -1
  };

  let rawPageData: RawIntroPageData;
  try {
    rawPageData = (await ProblemGenerator.getProblem(
      request
    )) as RawIntroPageData;
  } catch (e) {
    console.warn('Duplicate Instance Detected!');
    store.dispatch(duplicateInstanceDetected());
    return;
  }

  const tipText = rawPageData.data.markdown;
  const skillName = rawPageData.skill.name;

  const tip = (
    <ReactMarkdown className='SkillTip'>
      {tipText}
    </ReactMarkdown>
  );

  setSideBarElement(Panel.ResouceTips, tip);
  store.dispatch(setSkillBarName(skillName));
}

async function getFirstProblem(store: MiddlewareAPI<Dispatch<AnyAction>>) {
  const state = store.getState();
  const {
    currentProblemState: { setup: currentProblemSetup }
  } = state;

  bumpDifficulty(store, currentProblemSetup as ContentSetup);
}

/**
 *  The function that listens for and dispatches actions
 *  relevant to the embedding API.
 */
const progressionMiddleware =
  <S extends Dispatch<AnyAction>>(store: MiddlewareAPI<S, State>) =>
  (next: Dispatch<AnyAction>) =>
  <A extends Action>(action: A) => {
    // (boilerplate) Call the next dispatch method in the middleware chain.
    const returnValue = next(action);

    if (nextPageChosen.match(action)) {
      const problemSetup = action.payload;
      const state = store.getState();
      const {
        currentProblemState: { setup: currentProblemSetup }
      } = state;
      if (
        currentProblemSetup === undefined ||
        currentProblemSetup.progressionIndex !==
          problemSetup.progressionIndex ||
        currentProblemSetup.difficulty.secondary !==
          problemSetup.difficulty.secondary
      ) {
        store.dispatch(nextProblemTypeReached(problemSetup));
      }
    } else if (activityStart.match(action)) {
      const state = store.getState();
      const {
        ui: { userId },
        progression: { activityId, sessionId, cid, moduleInfo }
      } = state;
      const { savedUserState } = action.payload;
      if (userId !== null) {
        store.dispatch(progressStateLoading());
        const moduleState = savedUserState && savedUserState.moduleState;
        const stateVersion = savedUserState && savedUserState.stateVersion;

        if (moduleState && stateVersion === CURRENT_STATE_VERSION) {
          const moduleState = savedUserState.moduleState;

          // inject saved progress state, this comes externally where this 'game' is actually an
          // iframe like structure
          store.dispatch(progressStateLoaded({ moduleState, moduleInfo }));
        } else {
          getNextPage(store);
        }
        store.dispatch(getModuleTips());

        const event: ItsLoggerEvent = {
          cid,
          activityId,
          sessionId,
          uid: userId,
          eventType: ITSEventType.sessionStart,
          clientTime: Date.now()
        };

        store.dispatch(logEvent(event));
      }
    } else if (progressStateLoaded.match(action)) {
      getNextPage(store);
    } else if (moveOnToNextPage.match(action)) {
      getNextPage(store);
    } else if (introShown.match(action)) {
      getFirstProblem(store);
    } else if (getModuleTips.match(action)) {
      getSkillTips(store);
    } else if (switchModule.match(action)) {
      APP_CFG.switchModule(action.payload);
    } else if (forceProblemType.match(action)) {
      const state = store.getState();
      const problemSetup = state.currentProblemState.setup;
      const moduleInfo = state.progression.moduleInfo;
      const [progressionIndex, secondary] = action.payload;
      if (problemSetup) {
        const difficulty = { main: 0, secondary };
        const setup: ContentSetup = {
          ...problemSetup,
          difficulty,
          progressionIndex,
          moduleInfo
        };
        store.dispatch(progressionEndReached(false));
        store.dispatch(nextPageTypeAction(setup));
      }
    } else if (nextPageTypeAction.match(action)) {
      const state = store.getState();
      const {
        currentProblemState: { setup: currentProblemSetup }
      } = state;

      const newProblemSetup = action.payload;
      const dispatchGetModuleTips =
        newProblemSetup &&
        currentProblemSetup &&
        newProblemSetup.moduleInfo.skill !==
          currentProblemSetup.moduleInfo.skill;

      generateContentFromSetup(store, action.payload);
      if (dispatchGetModuleTips) {
        store.dispatch(getModuleTips());
      }
    } else if (submitResponse.match(action)) {
      const { pageContent, hasResponse, isValidResponse, setup } =
        store.getState().currentProblemState;
      if (hasResponse && isValidResponse && setup && pageContent) {
        const pageData = pageContent.pageData;
        const {
          score,
          ignoreList,
          playStats: { progress = 0, cumulativeTime }
        } = pageData;

        store.dispatch(
          responseEvaluated({
            setup,
            score,
            validOptions: ignoreList,
            progress,
            responseTime: cumulativeTime
          })
        );
        saveProgress(store.getState());
      }
    }

    return returnValue;
  };

/**
 * Create middleware that handles actions related to the Embedding API
 * (which allows running games inside the Copilot Student Client)
 * @param store Redux store
 */
export const createProgressionMiddleware = <D extends Dispatch<AnyAction>>(
  store: MiddlewareAPI<D>
) => {
  // Return the function that listens for and dispatches actions
  // relevant to the embedding API.
  return progressionMiddleware(store);
};
